import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  RevenueByModelRow,
  RevenueByTenantRow,
  RevenueDailyDay,
  RevenueSummaryResponse,
  RevenueWindow,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useRevenueByModel,
  useRevenueByTenant,
  useRevenueDaily,
  useRevenueSummary,
} from '../api/revenue.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';

/**
 * `/finance/revenue` — L1 + L2 margin dashboard (Epic #84 P11).
 *
 * Four sections, top-to-bottom:
 *   A. Summary card — window tab switcher (Today / 7d / 30d) with
 *      headline topup + usage numbers and the total gross margin.
 *   B. Daily trend — 30-day stacked bar chart (L1 blue, L2 amber).
 *   C. L2 by model — sortable table, lossy models highlighted.
 *   D. Top tenants — sortable table, emails link to the account
 *      detail page.
 *
 * Visual contract: DESIGN.md §5+§6 card layout on `surface-container`.
 * Zero hardcoded hex — the chart + highlight classes reference
 * Tailwind tokens defined in the tailwind.config palette (`primary`,
 * `tertiary`, `error`).
 *
 * Money display: every CNY / USD field is rendered via the helpers
 * below. Integer cents divided by 100 for display only — the API
 * payload itself is always integer cents.
 */

// ─── Formatters ──────────────────────────────────────────────────────

/**
 * Divide integer cents by 100 for display. Rendered with two decimal
 * places + comma separators. Accepts a sign-aware numeric value — a
 * negative `l2_profit` renders with a leading minus so operators read
 * lossy columns immediately.
 */
function fmtCny(cents: number): string {
  const yuan = cents / 100;
  return `¥${yuan.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtUsd(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format a margin percentage. The API gives us a fraction in `[0, 1]`
 * (or negative on losses); we multiply by 100 + two decimals. Zero is
 * rendered as "0.00%" rather than a dash — the backend returns 0 on
 * empty windows, and a dash would blur "no data" with "real zero".
 */
function fmtPct(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${(pct * 100).toFixed(2)}%`;
}

function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}

function fmtCurrency(cents: number, currency: DisplayCurrency): string {
  return currency === 'cny' ? fmtCny(cents) : fmtUsd(cents);
}

// ─── Page entry ──────────────────────────────────────────────────────

type RenderableWindow = Exclude<RevenueWindow, 'custom'>;
type DisplayCurrency = 'cny' | 'usd';

const WINDOW_LABEL_KEY: Record<RenderableWindow, I18nKey> = {
  today: 'revenue.window.today',
  '7d': 'revenue.window.7d',
  '30d': 'revenue.window.30d',
};

const WINDOW_OPTIONS: readonly RenderableWindow[] = ['today', '7d', '30d'];
const DISPLAY_CURRENCIES: readonly DisplayCurrency[] = ['cny', 'usd'];

const DEFAULT_DAYS = 30;
const TOP_TENANTS_LIMIT = 50;

export function RevenuePage(): JSX.Element {
  const t = useT();
  const [window, setWindow] = useState<RevenueWindow>('today');

  return (
    <section className="flex flex-col gap-8" data-testid="revenue-page">
      <header className="flex flex-col gap-3">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('revenue.title')}
        </h2>
        <p className="text-sm text-on-surface-variant">{t('revenue.subtitle')}</p>
      </header>

      <SummarySection window={window} onWindowChange={setWindow} />
      <DailyTrendSection />
      <ByModelSection />
      <TopTenantsSection />
    </section>
  );
}

// ─── Section A — summary ────────────────────────────────────────────

interface SummarySectionProps {
  readonly window: RevenueWindow;
  readonly onWindowChange: (w: RevenueWindow) => void;
}

function SummarySection({ window, onWindowChange }: SummarySectionProps): JSX.Element {
  const t = useT();
  const query = useRevenueSummary(window);

  return (
    <article
      data-testid="revenue-summary-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('revenue.summary.title')}
          </h3>
          <p className="text-xs text-outline">{t('revenue.summary.subtitle')}</p>
        </div>
        <div
          role="tablist"
          aria-label={t('revenue.summary.window_aria')}
          className="inline-flex rounded-lg bg-surface-container-low p-1"
        >
          {WINDOW_OPTIONS.map((opt) => {
            const active = window === opt;
            return (
              <button
                key={opt}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`revenue-window-${opt}`}
                onClick={() => onWindowChange(opt)}
                className={[
                  'rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em] transition-colors',
                  active
                    ? 'bg-surface-container-highest text-primary'
                    : 'text-outline hover:text-on-surface',
                ].join(' ')}
              >
                {t(WINDOW_LABEL_KEY[opt])}
              </button>
            );
          })}
        </div>
      </header>

      {query.isPending ? (
        <div
          data-testid="revenue-summary-loading"
          className="h-40 animate-pulse rounded-lg bg-surface-container-low"
          aria-hidden="true"
        />
      ) : null}

      {query.isError ? (
        <SectionError
          testId="revenue-summary-error"
          label={t('revenue.summary.error_label')}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {query.isSuccess ? <SummaryNumbers data={query.data} /> : null}
    </article>
  );
}

interface SummaryNumbersProps {
  readonly data: RevenueSummaryResponse;
}

function SummaryNumbers({ data }: SummaryNumbersProps): JSX.Element {
  const t = useT();
  const { topup, usage, total_gross_margin_cny_cents } = data;
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <MetricBlock
          testId="revenue-summary-topup"
          title={t('revenue.summary.topup_gmv')}
          primary={fmtCny(topup.cny_gross_cents)}
          secondary={t('revenue.summary.topup.orders', { n: fmtInt(topup.count) })}
          rows={[
            {
              label: t('revenue.summary.l1_profit'),
              value: fmtCny(topup.l1_profit_cny_cents),
              pct: fmtPct(topup.l1_margin_pct),
              tone: 'pos',
              testId: 'revenue-summary-topup-l1',
            },
            {
              label: t('revenue.summary.channel_fees'),
              value: `-${fmtCny(topup.cny_channel_fee_cents)}`,
              pct: '',
              tone: 'neg',
              testId: 'revenue-summary-topup-fees',
            },
            {
              label: t('revenue.summary.usd_credited'),
              value: fmtUsd(topup.usd_credited_cents),
              pct: '',
              tone: 'neutral',
              testId: 'revenue-summary-topup-usd',
            },
          ]}
        />
        <MetricBlock
          testId="revenue-summary-usage"
          title={t('revenue.summary.usage_gmv')}
          primary={fmtUsd(usage.usd_charged_cents)}
          secondary={t('revenue.summary.usage.api_calls', { n: fmtInt(usage.count) })}
          rows={[
            {
              label: t('revenue.summary.l2_profit'),
              value: fmtCny(usage.l2_profit_cny_cents),
              pct: fmtPct(usage.l2_margin_pct),
              tone: usage.l2_profit_cny_cents < 0 ? 'neg' : 'pos',
              testId: 'revenue-summary-usage-l2',
            },
            {
              label: t('revenue.summary.upstream_cost'),
              value: `-${fmtCny(usage.cny_upstream_cost_cents)}`,
              pct: '',
              tone: 'neg',
              testId: 'revenue-summary-usage-cost',
            },
          ]}
        />
      </div>
      <div
        data-testid="revenue-summary-total"
        className="flex flex-col items-center gap-2 rounded-lg bg-surface-container-low py-6"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
          {t('revenue.summary.total')}
        </span>
        <span
          className={[
            'font-mono text-4xl font-semibold tracking-tight',
            total_gross_margin_cny_cents < 0 ? 'text-error' : 'text-primary',
          ].join(' ')}
        >
          {fmtCny(total_gross_margin_cny_cents)}
        </span>
      </div>
    </div>
  );
}

interface MetricRow {
  readonly label: string;
  readonly value: string;
  readonly pct: string;
  readonly tone: 'pos' | 'neg' | 'neutral';
  readonly testId: string;
}

interface MetricBlockProps {
  readonly testId: string;
  readonly title: string;
  readonly primary: string;
  readonly secondary: string;
  readonly rows: readonly MetricRow[];
}

function MetricBlock({ testId, title, primary, secondary, rows }: MetricBlockProps): JSX.Element {
  return (
    <div
      data-testid={testId}
      className="flex flex-col gap-4 rounded-lg bg-surface-container-low p-6"
    >
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {title}
        </span>
        <span className="text-[11px] text-outline">{secondary}</span>
      </div>
      <span className="font-mono text-3xl font-semibold tracking-tight text-primary">
        {primary}
      </span>
      <div className="flex flex-col gap-2">
        {rows.map((r) => {
          const toneClass =
            r.tone === 'pos' ? 'text-primary' : r.tone === 'neg' ? 'text-error' : 'text-on-surface';
          return (
            <div
              key={r.label}
              data-testid={r.testId}
              className="flex items-baseline justify-between gap-4 border-t border-outline-variant/30 pt-2"
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {r.label}
              </span>
              <span className="flex items-baseline gap-3">
                <span className={`font-mono text-sm font-semibold ${toneClass}`}>{r.value}</span>
                {r.pct.length > 0 ? (
                  <span className="font-mono text-xs text-outline">{r.pct}</span>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Section B — daily trend ────────────────────────────────────────

function DailyTrendSection(): JSX.Element {
  const t = useT();
  const query = useRevenueDaily(DEFAULT_DAYS);

  return (
    <article
      data-testid="revenue-daily-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex flex-col gap-1">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('revenue.daily.title')}
        </h3>
        <p className="text-xs text-outline">
          {t('revenue.daily.subtitle_full', { days: String(DEFAULT_DAYS) })}
        </p>
      </header>

      {query.isPending ? (
        <div
          data-testid="revenue-daily-loading"
          className="h-48 animate-pulse rounded-lg bg-surface-container-low"
          aria-hidden="true"
        />
      ) : null}

      {query.isError ? (
        <SectionError
          testId="revenue-daily-error"
          label={t('revenue.daily.error_label')}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {query.isSuccess ? <DailyTrendChart days={query.data.days} /> : null}
    </article>
  );
}

interface DailyTrendChartProps {
  readonly days: readonly RevenueDailyDay[];
}

/**
 * Inline SVG stacked-bar chart. Pattern matches console-web's
 * DailyUsageChart — no charting library, each bar has a `<title>`
 * tooltip for hover readouts, every testid is stable for the unit
 * test to assert heights.
 *
 * Design choices:
 *   - L1 and L2 stack on the same vertical scale so operators read
 *     relative contribution at a glance.
 *   - Negative L2 days (lossy) draw below the x-axis using the
 *     `error` token. They eat visual height proportionally — if a
 *     lossy day is large it will dominate the frame, which is the
 *     intended alert behaviour.
 */
function DailyTrendChart({ days }: DailyTrendChartProps): JSX.Element {
  const t = useT();
  const PADDING_X = 24;
  const PADDING_Y = 24;
  const WIDTH = 960;
  const HEIGHT = 200;
  const BAR_GAP = 4;

  const maxPositive = useMemo(() => {
    let top = 0;
    for (const d of days) {
      const pos = Math.max(0, d.l1_profit_cny_cents) + Math.max(0, d.l2_profit_cny_cents);
      if (pos > top) top = pos;
    }
    return top;
  }, [days]);

  const maxNegative = useMemo(() => {
    let bottom = 0;
    for (const d of days) {
      const neg = Math.min(0, d.l2_profit_cny_cents) + Math.min(0, d.l1_profit_cny_cents);
      if (neg < bottom) bottom = neg;
    }
    return Math.abs(bottom);
  }, [days]);

  if (days.length === 0) {
    return (
      <div
        data-testid="revenue-daily-empty"
        className="flex items-center justify-center rounded-lg bg-surface-container-low py-16 text-sm text-outline"
      >
        {t('revenue.daily.empty')}
      </div>
    );
  }

  // The y-axis drawable height is split between the positive band
  // (most days) and the negative band (rare losses). When there are
  // no negative days the whole frame renders positive.
  const negScale = maxPositive + maxNegative === 0 ? 0 : maxNegative / (maxPositive + maxNegative);
  const drawableHeight = HEIGHT - PADDING_Y * 2;
  const zeroY = PADDING_Y + drawableHeight * (1 - negScale);
  const posHeight = drawableHeight * (1 - negScale);
  const negHeight = drawableHeight * negScale;
  const barWidth = Math.max(2, (WIDTH - PADDING_X * 2 - BAR_GAP * (days.length - 1)) / days.length);

  return (
    <svg
      role="img"
      aria-label={t('revenue.daily.aria_label', { days: String(days.length) })}
      data-testid="revenue-daily-chart"
      data-pos-height={Math.round(posHeight)}
      data-neg-height={Math.round(negHeight)}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      className="block h-48 w-full rounded-lg bg-surface-container-low"
    >
      <line
        x1={PADDING_X}
        x2={WIDTH - PADDING_X}
        y1={zeroY}
        y2={zeroY}
        strokeWidth={1}
        className="stroke-outline-variant"
      />
      {days.map((day, index) => {
        const l1 = day.l1_profit_cny_cents;
        const l2 = day.l2_profit_cny_cents;
        const x = PADDING_X + index * (barWidth + BAR_GAP);
        // Two-stage sizing per stack: compute the total stack height
        // first, then split proportionally. This guarantees that
        // `l1PosH + l2PosH === totalPosH <= posHeight` for every day
        // — the naive "scale each component independently against
        // maxPositive" version risks two Math.round() bumps pushing
        // the sum above posHeight and clipping outside the viewport.
        const l1Pos = Math.max(0, l1);
        const l2Pos = Math.max(0, l2);
        const dayPositive = l1Pos + l2Pos;
        const totalPosH =
          maxPositive === 0 ? 0 : Math.round((posHeight * dayPositive) / maxPositive);
        const l1PosH = dayPositive === 0 ? 0 : Math.round((totalPosH * l1Pos) / dayPositive);
        const l2PosH = totalPosH - l1PosH;
        const l2PosY = zeroY - l2PosH;
        const l1PosY = l2PosY - l1PosH;
        // Same two-stage split on the negative side so a day with both
        // L1 and L2 losses can't exceed the negative band either.
        const l1Neg = Math.abs(Math.min(0, l1));
        const l2Neg = Math.abs(Math.min(0, l2));
        const dayNegative = l1Neg + l2Neg;
        const totalNegH =
          maxNegative === 0 ? 0 : Math.round((negHeight * dayNegative) / maxNegative);
        const l2NegH = dayNegative === 0 ? 0 : Math.round((totalNegH * l2Neg) / dayNegative);
        const l1NegH = totalNegH - l2NegH;
        return (
          <g
            key={day.date}
            data-testid={`revenue-daily-bar-${index}`}
            data-date={day.date}
            data-l1-pos-h={l1PosH}
            data-l2-pos-h={l2PosH}
            data-l1-neg-h={l1NegH}
            data-l2-neg-h={l2NegH}
          >
            <title>
              {t('revenue.daily.tooltip', {
                date: day.date,
                l1: fmtCny(l1),
                l2: fmtCny(l2),
                topupCount: fmtInt(day.topup_count),
                usageCount: fmtInt(day.usage_count),
              })}
            </title>
            {l2PosH > 0 ? (
              <rect
                data-testid={`revenue-daily-bar-${index}-l2`}
                x={x}
                y={l2PosY}
                width={barWidth}
                height={l2PosH}
                rx={1}
                className="fill-tertiary"
              />
            ) : null}
            {l1PosH > 0 ? (
              <rect
                data-testid={`revenue-daily-bar-${index}-l1`}
                x={x}
                y={l1PosY}
                width={barWidth}
                height={l1PosH}
                rx={1}
                className="fill-primary"
              />
            ) : null}
            {l2NegH > 0 ? (
              <rect
                data-testid={`revenue-daily-bar-${index}-l2-neg`}
                x={x}
                y={zeroY}
                width={barWidth}
                height={l2NegH}
                rx={1}
                className="fill-error"
              />
            ) : null}
            {l1NegH > 0 ? (
              <rect
                data-testid={`revenue-daily-bar-${index}-l1-neg`}
                x={x}
                y={zeroY + l2NegH}
                width={barWidth}
                height={l1NegH}
                rx={1}
                className="fill-error"
              />
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Section C — by model ───────────────────────────────────────────

type ModelSortKey = 'profit' | 'count' | 'margin' | 'name';

function ByModelSection(): JSX.Element {
  const t = useT();
  const query = useRevenueByModel();
  const [sortKey, setSortKey] = useState<ModelSortKey>('profit');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [currency, setCurrency] = useState<DisplayCurrency>('cny');

  const sorted = useMemo(() => {
    if (!query.isSuccess) return [];
    const rows = query.data.models.slice();
    rows.sort((a, b) => {
      const base = compareModel(a, b, sortKey, currency);
      return sortDir === 'asc' ? base : -base;
    });
    return rows;
  }, [query.isSuccess, query.data, sortKey, sortDir, currency]);

  function toggle(key: ModelSortKey): void {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'name' ? 'asc' : 'desc');
  }

  return (
    <article
      data-testid="revenue-by-model-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('revenue.l2.title')}
          </h3>
          <p className="text-xs text-outline">{t('revenue.l2.subtitle')}</p>
        </div>
        <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-outline">
          <span>{t('revenue.l2.currency.label')}</span>
          <select
            aria-label={t('revenue.l2.currency.aria')}
            data-testid="revenue-by-model-currency"
            value={currency}
            onChange={(event) => setCurrency(event.currentTarget.value as DisplayCurrency)}
            className="h-8 rounded-lg border border-outline-variant bg-surface-container-low px-3 font-mono text-[12px] uppercase tracking-normal text-on-surface outline-none transition-colors focus:border-primary"
          >
            {DISPLAY_CURRENCIES.map((value) => (
              <option key={value} value={value}>
                {value.toUpperCase()}
              </option>
            ))}
          </select>
        </label>
      </header>
      {query.isPending ? (
        <div
          data-testid="revenue-by-model-loading"
          className="h-32 animate-pulse rounded-lg bg-surface-container-low"
          aria-hidden="true"
        />
      ) : null}
      {query.isError ? (
        <SectionError
          testId="revenue-by-model-error"
          label={t('revenue.l2.error_label')}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}
      {query.isSuccess && sorted.length === 0 ? (
        <p
          data-testid="revenue-by-model-empty"
          className="rounded-lg bg-surface-container-low py-12 text-center text-sm text-outline"
        >
          {t('revenue.l2.empty')}
        </p>
      ) : null}
      {query.isSuccess && sorted.length > 0 ? (
        <div className="overflow-hidden rounded-xl bg-surface-container-low">
          <table
            data-testid="revenue-by-model-table"
            className="w-full border-separate border-spacing-0"
          >
            <thead>
              <tr className="bg-surface-container-high">
                <ModelHeader
                  label={t('revenue.l2.column.model')}
                  active={sortKey === 'name'}
                  dir={sortDir}
                  onClick={() => toggle('name')}
                  align="left"
                  testId="revenue-by-model-sort-name"
                />
                <ModelHeader
                  label={t('revenue.l2.column.usage')}
                  active={sortKey === 'count'}
                  dir={sortDir}
                  onClick={() => toggle('count')}
                  align="right"
                  testId="revenue-by-model-sort-count"
                />
                <ModelHeader
                  label={t('revenue.l2.column.charged', { currency: currency.toUpperCase() })}
                  active={false}
                  dir={sortDir}
                  onClick={() => undefined}
                  align="right"
                  testId="revenue-by-model-col-charged"
                  sortable={false}
                />
                <ModelHeader
                  label={t('revenue.l2.column.cost', { currency: currency.toUpperCase() })}
                  active={false}
                  dir={sortDir}
                  onClick={() => undefined}
                  align="right"
                  testId="revenue-by-model-col-cost"
                  sortable={false}
                />
                <ModelHeader
                  label={t('revenue.l2.column.l2_profit')}
                  active={sortKey === 'profit'}
                  dir={sortDir}
                  onClick={() => toggle('profit')}
                  align="right"
                  testId="revenue-by-model-sort-profit"
                />
                <ModelHeader
                  label={t('revenue.l2.column.l2_pct')}
                  active={sortKey === 'margin'}
                  dir={sortDir}
                  onClick={() => toggle('margin')}
                  align="right"
                  testId="revenue-by-model-sort-margin"
                />
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <ModelRow
                  key={`${row.model_vendor}/${row.model_name}`}
                  row={row}
                  zebra={i % 2 === 0 ? 'odd' : 'even'}
                  currency={currency}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </article>
  );
}

function compareModel(
  a: RevenueByModelRow,
  b: RevenueByModelRow,
  key: ModelSortKey,
  currency: DisplayCurrency,
): number {
  switch (key) {
    case 'profit':
      return modelProfit(a, currency) - modelProfit(b, currency);
    case 'count':
      return a.usage_count - b.usage_count;
    case 'margin':
      return a.l2_margin_pct - b.l2_margin_pct;
    case 'name':
      return `${a.model_vendor}/${a.model_name}`.localeCompare(`${b.model_vendor}/${b.model_name}`);
  }
}

interface ModelRowProps {
  readonly row: RevenueByModelRow;
  readonly zebra: 'odd' | 'even';
  readonly currency: DisplayCurrency;
}

function modelCharged(row: RevenueByModelRow, currency: DisplayCurrency): number {
  return currency === 'cny' ? row.cny_charged_cents : row.usd_charged_cents;
}

function modelCost(row: RevenueByModelRow, currency: DisplayCurrency): number {
  return currency === 'cny' ? row.cny_upstream_cost_cents : row.usd_upstream_cost_cents;
}

function modelProfit(row: RevenueByModelRow, currency: DisplayCurrency): number {
  return currency === 'cny' ? row.l2_profit_cny_cents : row.l2_profit_usd_cents;
}

function ModelRow({ row, zebra, currency }: ModelRowProps): JSX.Element {
  const t = useT();
  const zebraClass = zebra === 'odd' ? 'bg-surface-container-low' : 'bg-surface-container';
  const profit = modelProfit(row, currency);
  // Lossy: red for negative margin, amber for thin (<2%) but positive.
  const lossy = profit < 0;
  const thin = !lossy && row.l2_margin_pct >= 0 && row.l2_margin_pct < 0.02;
  const highlightClass = lossy ? 'text-error' : thin ? 'text-tertiary' : 'text-on-surface';
  const flagLabel = lossy ? t('revenue.l2.flag.loss') : thin ? t('revenue.l2.flag.thin') : null;
  // Machine-readable tag — tests assert on `data-flag`, so it must stay
  // in the canonical ASCII form regardless of locale.
  const flagTag = lossy ? 'LOSS' : thin ? 'THIN' : 'ok';
  return (
    <tr
      data-testid={`revenue-by-model-row-${row.model_vendor}-${row.model_name}`}
      className={`${zebraClass} transition-colors hover:bg-surface-bright`}
      data-flag={flagTag}
    >
      <td className="px-4 py-3 text-[13px] text-on-surface">
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold">{row.model_name}</span>
          <span className="font-mono text-[11px] text-outline">{row.model_vendor}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-right font-mono text-[12px] text-on-surface">
        {fmtInt(row.usage_count)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-[12px] text-on-surface">
        {fmtCurrency(modelCharged(row, currency), currency)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-[12px] text-on-surface">
        {fmtCurrency(modelCost(row, currency), currency)}
      </td>
      <td className={`px-4 py-3 text-right font-mono text-[12px] ${highlightClass}`}>
        {fmtCurrency(profit, currency)}
      </td>
      <td
        data-testid={`revenue-by-model-row-${row.model_vendor}-${row.model_name}-margin`}
        className={`px-4 py-3 text-right font-mono text-[12px] ${highlightClass}`}
      >
        <span className="inline-flex items-center gap-2">
          {flagLabel !== null ? (
            <span
              className={[
                'rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em]',
                lossy
                  ? 'bg-error-container text-on-error-container'
                  : 'bg-tertiary-container text-on-tertiary-container',
              ].join(' ')}
            >
              {flagLabel}
            </span>
          ) : null}
          <span>{fmtPct(row.l2_margin_pct)}</span>
        </span>
      </td>
    </tr>
  );
}

// ─── Section D — top tenants ────────────────────────────────────────

type TenantSortKey = 'total' | 'l1' | 'l2' | 'topup' | 'usage' | 'email';

function TopTenantsSection(): JSX.Element {
  const t = useT();
  const query = useRevenueByTenant(TOP_TENANTS_LIMIT);
  const [sortKey, setSortKey] = useState<TenantSortKey>('total');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    if (!query.isSuccess) return [];
    const rows = query.data.tenants.slice();
    rows.sort((a, b) => {
      const base = compareTenant(a, b, sortKey);
      return sortDir === 'asc' ? base : -base;
    });
    return rows;
  }, [query.isSuccess, query.data, sortKey, sortDir]);

  function toggle(key: TenantSortKey): void {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'email' ? 'asc' : 'desc');
  }

  return (
    <article
      data-testid="revenue-by-tenant-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex flex-col gap-1">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('revenue.top_tenants.title')}
        </h3>
        <p className="text-xs text-outline">
          {t('revenue.top_tenants.subtitle', { n: String(TOP_TENANTS_LIMIT) })}
        </p>
      </header>
      {query.isPending ? (
        <div
          data-testid="revenue-by-tenant-loading"
          className="h-32 animate-pulse rounded-lg bg-surface-container-low"
          aria-hidden="true"
        />
      ) : null}
      {query.isError ? (
        <SectionError
          testId="revenue-by-tenant-error"
          label={t('revenue.top_tenants.error_label')}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}
      {query.isSuccess && sorted.length === 0 ? (
        <p
          data-testid="revenue-by-tenant-empty"
          className="rounded-lg bg-surface-container-low py-12 text-center text-sm text-outline"
        >
          {t('revenue.top_tenants.empty')}
        </p>
      ) : null}
      {query.isSuccess && sorted.length > 0 ? (
        <div className="overflow-hidden rounded-xl bg-surface-container-low">
          <table
            data-testid="revenue-by-tenant-table"
            className="w-full border-separate border-spacing-0"
          >
            <thead>
              <tr className="bg-surface-container-high">
                <TenantHeader
                  label={t('revenue.top_tenants.column.email')}
                  active={sortKey === 'email'}
                  dir={sortDir}
                  onClick={() => toggle('email')}
                  align="left"
                  testId="revenue-by-tenant-sort-email"
                />
                <TenantHeader
                  label={t('revenue.top_tenants.column.topups')}
                  active={sortKey === 'topup'}
                  dir={sortDir}
                  onClick={() => toggle('topup')}
                  align="right"
                  testId="revenue-by-tenant-sort-topup"
                />
                <TenantHeader
                  label={t('revenue.top_tenants.column.l1_profit')}
                  active={sortKey === 'l1'}
                  dir={sortDir}
                  onClick={() => toggle('l1')}
                  align="right"
                  testId="revenue-by-tenant-sort-l1"
                />
                <TenantHeader
                  label={t('revenue.top_tenants.column.usage')}
                  active={sortKey === 'usage'}
                  dir={sortDir}
                  onClick={() => toggle('usage')}
                  align="right"
                  testId="revenue-by-tenant-sort-usage"
                />
                <TenantHeader
                  label={t('revenue.top_tenants.column.l2_profit')}
                  active={sortKey === 'l2'}
                  dir={sortDir}
                  onClick={() => toggle('l2')}
                  align="right"
                  testId="revenue-by-tenant-sort-l2"
                />
                <TenantHeader
                  label={t('revenue.top_tenants.column.total')}
                  active={sortKey === 'total'}
                  dir={sortDir}
                  onClick={() => toggle('total')}
                  align="right"
                  testId="revenue-by-tenant-sort-total"
                />
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <TenantRow key={row.account_id} row={row} zebra={i % 2 === 0 ? 'odd' : 'even'} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </article>
  );
}

function compareTenant(a: RevenueByTenantRow, b: RevenueByTenantRow, key: TenantSortKey): number {
  switch (key) {
    case 'total':
      return a.total_cny_cents - b.total_cny_cents;
    case 'l1':
      return a.l1_profit_cny_cents - b.l1_profit_cny_cents;
    case 'l2':
      return a.l2_profit_cny_cents - b.l2_profit_cny_cents;
    case 'topup':
      return a.topup_count - b.topup_count;
    case 'usage':
      return a.usage_count - b.usage_count;
    case 'email':
      return a.account_email.localeCompare(b.account_email);
  }
}

interface TenantRowProps {
  readonly row: RevenueByTenantRow;
  readonly zebra: 'odd' | 'even';
}

function TenantRow({ row, zebra }: TenantRowProps): JSX.Element {
  const t = useT();
  const zebraClass = zebra === 'odd' ? 'bg-surface-container-low' : 'bg-surface-container';
  const emailLabel =
    row.account_email.length > 0 ? row.account_email : t('revenue.top_tenants.no_email');
  const totalLossy = row.total_cny_cents < 0;
  return (
    <tr
      data-testid={`revenue-by-tenant-row-${row.account_id}`}
      className={`${zebraClass} transition-colors hover:bg-surface-bright`}
    >
      <td className="px-4 py-3 text-[13px]">
        <Link
          to={`/customers/accounts/${row.account_id}`}
          data-testid={`revenue-by-tenant-row-${row.account_id}-email`}
          className="text-on-surface underline decoration-outline-variant underline-offset-2 hover:text-primary hover:decoration-primary"
        >
          {emailLabel}
        </Link>
        <div className="mt-0.5 font-mono text-[11px] text-outline">{row.account_id}</div>
      </td>
      <td className="px-4 py-3 text-right font-mono text-[12px] text-on-surface">
        {fmtInt(row.topup_count)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-[12px] text-on-surface">
        {fmtCny(row.l1_profit_cny_cents)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-[12px] text-on-surface">
        {fmtInt(row.usage_count)}
      </td>
      <td
        className={`px-4 py-3 text-right font-mono text-[12px] ${
          row.l2_profit_cny_cents < 0 ? 'text-error' : 'text-on-surface'
        }`}
      >
        {fmtCny(row.l2_profit_cny_cents)}
      </td>
      <td
        data-testid={`revenue-by-tenant-row-${row.account_id}-total`}
        className={`px-4 py-3 text-right font-mono text-[12px] font-semibold ${
          totalLossy ? 'text-error' : 'text-primary'
        }`}
      >
        {fmtCny(row.total_cny_cents)}
      </td>
    </tr>
  );
}

// ─── Shared UI atoms ────────────────────────────────────────────────

interface SectionErrorProps {
  readonly testId: string;
  readonly label: string;
  readonly error: unknown;
  readonly onRetry: () => void;
}

function SectionError({ testId, label, error, onRetry }: SectionErrorProps): JSX.Element {
  const t = useT();
  const message =
    error instanceof OpsApiError ? `${error.code}: ${error.message}` : t('common.network_error');
  return (
    <div
      role="alert"
      data-testid={testId}
      className="flex items-center justify-between gap-4 rounded-lg bg-error-container px-6 py-4 text-on-error-container"
    >
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold">{label}</span>
        <span className="text-xs">{message}</span>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
      >
        {t('common.retry')}
      </button>
    </div>
  );
}

interface HeaderProps {
  readonly label: string;
  readonly active: boolean;
  readonly dir: 'asc' | 'desc';
  readonly onClick: () => void;
  readonly align: 'left' | 'right';
  readonly testId: string;
  readonly sortable?: boolean;
}

function ModelHeader(props: HeaderProps): JSX.Element {
  return <GenericHeader {...props} />;
}

function TenantHeader(props: HeaderProps): JSX.Element {
  return <GenericHeader {...props} />;
}

function GenericHeader({
  label,
  active,
  dir,
  onClick,
  align,
  testId,
  sortable = true,
}: HeaderProps): JSX.Element {
  const alignClass = align === 'left' ? 'text-left' : 'text-right';
  const ariaSort = active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`px-4 py-3 ${alignClass} text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant`}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onClick}
          data-testid={testId}
          className="inline-flex items-center gap-1 hover:text-on-surface focus-visible:outline-none focus-visible:text-on-surface"
        >
          <span>{label}</span>
          <span aria-hidden="true" className="text-[9px] text-outline-variant">
            {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
          </span>
        </button>
      ) : (
        <span data-testid={testId}>{label}</span>
      )}
    </th>
  );
}
